import { Data, Effect, Schema, SchemaIssue, Struct } from 'effect';

const mutableStruct = <const Fields extends Schema.Struct.Fields>(fields: Fields) =>
  Schema.Struct(fields).mapFields(Struct.map(Schema.mutableKey));

export const dashboardUnknownRecordCodec = Schema.Record(
  Schema.String,
  Schema.mutableKey(Schema.Unknown),
);

const passthrough = <const Fields extends Schema.Struct.Fields>(fields: Fields) =>
  Schema.StructWithRest(mutableStruct(fields), [dashboardUnknownRecordCodec]);

const mutableArray = <S extends Schema.Top>(item: S) => Schema.mutable(Schema.Array(item));
const nonEmptyArray = <S extends Schema.Top>(item: S) =>
  mutableArray(item).check(
    Schema.makeFilter((items) => items.length > 0 || 'Array must contain at least 1 element(s)'),
  );
const NonEmptyString = Schema.String.check(Schema.isMinLength(1));
const ZodInteger = Schema.Number.check(
  Schema.makeFilter((value) => Number.isInteger(value) || 'Expected integer, received float'),
);
const PositiveInt = ZodInteger.check(Schema.isGreaterThan(0));
const NonNegativeInt = ZodInteger.check(Schema.isGreaterThanOrEqualTo(0));

export const tableColumnTypeCodec = Schema.Literals([
  'text',
  'integer',
  'real',
  'boolean',
  'date',
  'datetime',
  'currency',
  'json',
]);

export const tableColumnCodec = mutableStruct({
  name: NonEmptyString,
  original_name: Schema.optionalKey(NonEmptyString),
  type: tableColumnTypeCodec,
  required: Schema.optionalKey(Schema.Boolean),
  unique: Schema.optionalKey(Schema.Boolean),
  default: Schema.optionalKey(
    Schema.NullOr(Schema.Union([Schema.String, Schema.Number, Schema.Boolean])),
  ),
  sort_order: Schema.optionalKey(ZodInteger),
});

export const createTableRequestCodec = mutableStruct({
  name: NonEmptyString,
  template: Schema.optionalKey(Schema.NullOr(NonEmptyString)),
  columns: nonEmptyArray(tableColumnCodec),
});

export const tableSchemaCodec = passthrough({
  id: Schema.optionalKey(NonEmptyString),
  name: NonEmptyString,
  template: Schema.optionalKey(Schema.NullOr(Schema.String)),
  columns: mutableArray(tableColumnCodec),
  created_at: Schema.optionalKey(Schema.NullOr(Schema.String)),
});

export const createTableResponseCodec = tableSchemaCodec;

export const tableRowsResponseCodec = passthrough({
  columns: Schema.optionalKey(
    mutableArray(
      mutableStruct({
        name: NonEmptyString,
        type: NonEmptyString,
        required: Schema.optionalKey(Schema.Boolean),
      }),
    ),
  ),
  rows: mutableArray(dashboardUnknownRecordCodec),
  total: NonNegativeInt,
  limit: Schema.optionalKey(PositiveInt),
  offset: Schema.optionalKey(NonNegativeInt),
});

export const createAgentRequestCodec = mutableStruct({
  name: NonEmptyString,
  table_id: Schema.optionalKey(NonEmptyString),
  system: Schema.optionalKey(NonEmptyString),
  model: Schema.optionalKey(NonEmptyString),
  output_schema: Schema.optionalKey(Schema.Unknown),
});

export const agentListItemCodec = passthrough({
  id: NonEmptyString,
  object: Schema.optionalKey(Schema.Literal('agent')),
  name: NonEmptyString,
  model: Schema.optionalKey(Schema.NullOr(Schema.String)),
  system: Schema.optionalKey(Schema.NullOr(Schema.String)),
  output_schema: Schema.optionalKey(Schema.NullOr(Schema.Unknown)),
  table_id: Schema.optionalKey(Schema.NullOr(Schema.String)),
  email: Schema.optionalKey(Schema.NullOr(Schema.String)),
  version: Schema.optionalKey(ZodInteger),
  created_at: Schema.optionalKey(Schema.String),
  updated_at: Schema.optionalKey(Schema.String),
});

export const createAgentResponseCodec = agentListItemCodec;

export const listAgentsResponseCodec = passthrough({
  data: mutableArray(agentListItemCodec),
  has_more: Schema.optionalKey(Schema.Boolean),
  next_cursor: Schema.optionalKey(Schema.NullOr(Schema.String)),
});

export const inboxCodec = passthrough({
  id: NonEmptyString,
  object: Schema.optionalKey(Schema.Literal('inbox')),
  name: Schema.optionalKey(Schema.String),
  email: NonEmptyString,
  collection_id: Schema.optionalKey(Schema.NullOr(Schema.String)),
  agent_id: Schema.optionalKey(Schema.NullOr(Schema.String)),
  table_id: Schema.optionalKey(Schema.NullOr(Schema.String)),
  prompt: Schema.optionalKey(Schema.NullOr(Schema.String)),
  allowed_senders: Schema.optionalKey(Schema.NullOr(mutableArray(Schema.String))),
  from_name: Schema.optionalKey(Schema.NullOr(Schema.String)),
  active: Schema.Boolean,
  created_at: Schema.optionalKey(Schema.String),
});

export const createInboxRequestCodec = mutableStruct({
  name: NonEmptyString,
  agent_id: Schema.optionalKey(NonEmptyString),
  table_id: Schema.optionalKey(NonEmptyString),
  prompt: Schema.optionalKey(NonEmptyString),
});

export const inboxListResponseCodec = passthrough({
  data: mutableArray(inboxCodec),
  has_more: Schema.optionalKey(Schema.Boolean),
  next_cursor: Schema.optionalKey(Schema.NullOr(Schema.String)),
});

export const documentListItemCodec = passthrough({
  id: NonEmptyString,
  file_name: Schema.optionalKey(Schema.NullOr(Schema.String)),
  phase: Schema.optionalKey(Schema.String),
  status: Schema.optionalKey(Schema.String),
  created_at: Schema.optionalKey(Schema.String),
  inserted_at: Schema.optionalKey(Schema.String),
  updated_at: Schema.optionalKey(Schema.String),
  total_pages: Schema.optionalKey(NonNegativeInt),
  pages_completed: Schema.optionalKey(NonNegativeInt),
  thumbnail_url: Schema.optionalKey(Schema.String),
  is_public: Schema.optionalKey(Schema.Boolean),
  source: Schema.optionalKey(Schema.String),
});

export const documentListResponseCodec = passthrough({
  data: mutableArray(documentListItemCodec),
  has_more: Schema.optionalKey(Schema.Boolean),
  next_cursor: Schema.optionalKey(Schema.NullOr(Schema.String)),
  documents: Schema.optionalKey(mutableArray(documentListItemCodec)),
});

export const collectionListItemCodec = passthrough({
  id: NonEmptyString,
  name: NonEmptyString,
  slug: Schema.optionalKey(Schema.String),
  document_count: Schema.optionalKey(NonNegativeInt),
});

export const collectionListResponseCodec = Schema.Union([
  mutableArray(collectionListItemCodec),
  passthrough({ data: mutableArray(collectionListItemCodec) }),
  passthrough({ collections: mutableArray(collectionListItemCodec) }),
]);

export const collectionDetailCodec = passthrough({
  id: NonEmptyString,
  name: NonEmptyString,
  slug: Schema.optionalKey(Schema.String),
  documents: Schema.optionalKey(
    mutableArray(
      passthrough({
        id: NonEmptyString,
        name: NonEmptyString,
      }),
    ),
  ),
});

export const tableListItemCodec = passthrough({
  id: NonEmptyString,
  name: NonEmptyString,
  template: Schema.optionalKey(Schema.NullOr(Schema.String)),
  agent_id: Schema.optionalKey(Schema.NullOr(Schema.String)),
  created_at: Schema.optionalKey(Schema.String),
});

export const listTablesResponseCodec = passthrough({
  data: mutableArray(tableListItemCodec),
  has_more: Schema.optionalKey(Schema.Boolean),
  next_cursor: Schema.optionalKey(Schema.NullOr(Schema.String)),
});

export const workflowQualityPresetCodec = Schema.Literals(['fast', 'balanced', 'high']);

export const workflowFileInputCodec = mutableStruct({
  file_id: NonEmptyString,
});

export const invoiceExtractionRunRequestCodec = passthrough({
  inputs: nonEmptyArray(workflowFileInputCodec),
  table_id: Schema.optionalKey(NonEmptyString),
  quality: workflowQualityPresetCodec.pipe(
    Schema.withDecodingDefaultKey(Effect.succeed('balanced' as const)),
  ),
});

const invoiceExtractionExportsCodec = passthrough({
  csv_url: NonEmptyString,
  json_url: NonEmptyString,
  xml_url: Schema.optionalKey(NonEmptyString),
  xlsx_url: Schema.optionalKey(NonEmptyString),
});

export const invoiceExtractionRunResponseCodec = passthrough({
  id: NonEmptyString,
  run_id: NonEmptyString,
  object: Schema.Literal('run'),
  status: NonEmptyString,
  workflow_name: Schema.Literal('invoice-extraction'),
  workflow_label: NonEmptyString,
  workflow_version: PositiveInt,
  quality: workflowQualityPresetCodec,
  inputs: mutableArray(workflowFileInputCodec),
  table_id: Schema.NullOr(NonEmptyString),
  agent_id: Schema.optionalKey(Schema.NullOr(NonEmptyString)),
  events_url: Schema.optionalKey(NonEmptyString),
  stream_url: Schema.optionalKey(NonEmptyString),
  rows_url: Schema.optionalKey(NonEmptyString),
  exports: Schema.optionalKey(invoiceExtractionExportsCodec),
  created_at: NonEmptyString,
});

export const invoiceExtractionRunListResponseCodec = passthrough({
  object: Schema.Literal('list'),
  data: mutableArray(invoiceExtractionRunResponseCodec),
  has_more: Schema.Boolean,
  next_cursor: Schema.NullOr(Schema.String),
});

export const invoiceExtractionExceptionCodec = passthrough({
  id: NonEmptyString,
  object: Schema.Literal('invoice_exception'),
  run_id: NonEmptyString,
  table_id: NonEmptyString,
  row_id: Schema.optionalKey(Schema.NullOr(NonEmptyString)),
  file_id: Schema.optionalKey(Schema.NullOr(NonEmptyString)),
  field: Schema.optionalKey(Schema.NullOr(NonEmptyString)),
  code: Schema.Literals([
    'missing_required_field',
    'document_parse_failed',
    'empty_result',
    'run_failed',
    'validation_warning',
  ]),
  severity: Schema.Literals(['warning', 'error']),
  status: Schema.Literals(['open', 'resolved']),
  message: NonEmptyString,
  value: Schema.optionalKey(Schema.Unknown),
  source_page: Schema.optionalKey(Schema.NullOr(PositiveInt)),
  source_bbox: Schema.optionalKey(Schema.NullOr(NonEmptyString)),
  created_at: NonEmptyString,
});

export const invoiceExtractionExceptionListResponseCodec = passthrough({
  object: Schema.Literal('list'),
  data: mutableArray(invoiceExtractionExceptionCodec),
  has_more: Schema.optionalKey(Schema.Boolean),
  next_cursor: Schema.optionalKey(Schema.NullOr(Schema.String)),
});

const formatStandardSchemaIssue = SchemaIssue.makeFormatterStandardSchemaV1();

export type DashboardHttpWireOperation =
  | 'create-table-request'
  | 'table-schema'
  | 'table-rows-response'
  | 'create-agent-request'
  | 'agent'
  | 'agent-list-response'
  | 'inbox'
  | 'inbox-list-response'
  | 'document-list-response'
  | 'table'
  | 'table-list-response'
  | 'invoice-run-request'
  | 'invoice-run-response'
  | 'invoice-run-list-response'
  | 'invoice-exception-list-response';

export type DashboardHttpWireIssue = {
  readonly message: string;
  readonly path: ReadonlyArray<PropertyKey>;
};

export class DashboardHttpWireDecodeError extends Data.TaggedError('DashboardHttpWireDecodeError')<{
  readonly operation: DashboardHttpWireOperation;
  readonly issues: ReadonlyArray<DashboardHttpWireIssue>;
}> {}

function dashboardHttpIssues(error: unknown): ReadonlyArray<DashboardHttpWireIssue> {
  if (!Schema.isSchemaError(error)) {
    return [{ message: error instanceof Error ? error.message : String(error), path: [] }];
  }
  return formatStandardSchemaIssue(error.issue).issues.map((issue) => ({
    message: issue.message,
    path: issue.path?.map((segment) => (typeof segment === 'object' ? segment.key : segment)) ?? [],
  }));
}

function decodeDashboardHttpEffect<S extends Schema.ConstraintDecoder<unknown>>(
  operation: DashboardHttpWireOperation,
  codec: S,
  input: unknown,
): Effect.Effect<S['Type'], DashboardHttpWireDecodeError> {
  return Schema.decodeUnknownEffect(codec, { errors: 'all' })(input).pipe(
    Effect.mapError(
      (error) =>
        new DashboardHttpWireDecodeError({ operation, issues: dashboardHttpIssues(error) }),
    ),
  );
}

export const decodeCreateTableRequestEffect = (input: unknown) =>
  decodeDashboardHttpEffect('create-table-request', createTableRequestCodec, input);
export const decodeTableSchemaEffect = (input: unknown) =>
  decodeDashboardHttpEffect('table-schema', tableSchemaCodec, input);
export const decodeTableRowsResponseEffect = (input: unknown) =>
  decodeDashboardHttpEffect('table-rows-response', tableRowsResponseCodec, input);
export const decodeCreateAgentRequestEffect = (input: unknown) =>
  decodeDashboardHttpEffect('create-agent-request', createAgentRequestCodec, input);
export const decodeAgentEffect = (input: unknown) =>
  decodeDashboardHttpEffect('agent', agentListItemCodec, input);
export const decodeAgentListResponseEffect = (input: unknown) =>
  decodeDashboardHttpEffect('agent-list-response', listAgentsResponseCodec, input);
export const decodeInboxEffect = (input: unknown) =>
  decodeDashboardHttpEffect('inbox', inboxCodec, input);
export const decodeInboxListResponseEffect = (input: unknown) =>
  decodeDashboardHttpEffect('inbox-list-response', inboxListResponseCodec, input);
export const decodeDocumentListResponseEffect = (input: unknown) =>
  decodeDashboardHttpEffect('document-list-response', documentListResponseCodec, input);
export const decodeTableEffect = (input: unknown) =>
  decodeDashboardHttpEffect('table', tableListItemCodec, input);
export const decodeTableListResponseEffect = (input: unknown) =>
  decodeDashboardHttpEffect('table-list-response', listTablesResponseCodec, input);
export const decodeInvoiceRunRequestEffect = (input: unknown) =>
  decodeDashboardHttpEffect('invoice-run-request', invoiceExtractionRunRequestCodec, input);
export const decodeInvoiceRunResponseEffect = (input: unknown) =>
  decodeDashboardHttpEffect('invoice-run-response', invoiceExtractionRunResponseCodec, input);
export const decodeInvoiceRunListResponseEffect = (input: unknown) =>
  decodeDashboardHttpEffect(
    'invoice-run-list-response',
    invoiceExtractionRunListResponseCodec,
    input,
  );
export const decodeInvoiceExceptionListResponseEffect = (input: unknown) =>
  decodeDashboardHttpEffect(
    'invoice-exception-list-response',
    invoiceExtractionExceptionListResponseCodec,
    input,
  );
